import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Minus, Plus } from 'lucide-react';
import './QuantityInput.css';

/**
 * QuantityInput Component
 * 
 * Compact numeric stepper with minus/plus buttons.
 * Pixel-perfect implementation from Figma via MCP.
 * 
 * Figma node: 3797:15947
 * 
 * States (from Figma):
 * - Default:                  bg gray-0, border gray-4
 * - Hover:                    bg gray-025, border gray-10
 * - Focus or Populated Hover: bg gray-0, border gray-4
 * - Focus & Filled:           bg gray-025, border gray-10
 * - Error:                    bg gray-025, border ui-error, error tooltip
 * 
 * Structure:
 * - input container (border, bg)
 *   - content (flex row)
 *     - minus button (Button Tertiary / lucide minus)
 *     - value (text-center, input typography)
 *     - plus button (Button Tertiary / lucide plus)
 *   - error tooltip (absolute, below)
 */
const QuantityInput = ({
  value: controlledValue,
  defaultValue = 1,
  min = 0,
  max = 99,
  step = 1,
  onChange,
  error = '',
  disabled = false,
  className = '',
  id,
  name,
  ...props
}) => {
  // Controlled vs uncontrolled
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  // Focus state
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const wrapperRef = useRef(null);

  const setValue = useCallback((newValue) => {
    const clamped = Math.min(max, Math.max(min, newValue));
    if (!isControlled) {
      setInternalValue(clamped);
    }
    onChange?.(clamped);
  }, [min, max, isControlled, onChange]);

  const handleDecrement = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setValue(currentValue - step);
  }, [currentValue, step, disabled, setValue]);

  const handleIncrement = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setValue(currentValue + step);
  }, [currentValue, step, disabled, setValue]);

  // Keyboard support
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      e.preventDefault();
      setValue(currentValue + step);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setValue(currentValue - step);
    }
  }, [currentValue, step, disabled, setValue]);

  // Determine visual state
  const hasError = !!error;
  const isAtMin = currentValue <= min;
  const isAtMax = currentValue >= max;

  // Build CSS classes
  const wrapperClasses = [
    'qty-input',
    isHovered && !isFocused && !hasError && !disabled && 'qty-input--hover',
    isFocused && 'qty-input--focus',
    hasError && 'qty-input--error',
    disabled && 'qty-input--disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={wrapperRef}
      className={wrapperClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      <div
        className="qty-input__field"
        role="group"
        aria-label="Quantity"
        tabIndex={disabled ? undefined : 0}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
      >
        <div className="qty-input__content">
          {/* Minus button */}
          <button
            type="button"
            className={`qty-input__btn qty-input__btn--minus${isAtMin ? ' qty-input__btn--disabled' : ''}`}
            onClick={handleDecrement}
            disabled={disabled || isAtMin}
            tabIndex={-1}
            aria-label="Decrease quantity"
          >
            <Minus size={20} strokeWidth={1.5} />
          </button>

          {/* Value display */}
          <div className="qty-input__value-wrap">
            <span className="qty-input__value typo-input">
              {currentValue}
            </span>
            {/* Hidden native input for form submission */}
            <input
              type="hidden"
              id={id}
              name={name}
              value={currentValue}
            />
          </div>

          {/* Plus button */}
          <button
            type="button"
            className={`qty-input__btn qty-input__btn--plus${isAtMax ? ' qty-input__btn--disabled' : ''}`}
            onClick={handleIncrement}
            disabled={disabled || isAtMax}
            tabIndex={-1}
            aria-label="Increase quantity"
          >
            <Plus size={20} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Error tooltip */}
      {hasError && (
        <div className="qty-input__error">
          <p className="qty-input__error-text">{error}</p>
        </div>
      )}
    </div>
  );
};

QuantityInput.propTypes = {
  /** Controlled value */
  value: PropTypes.number,
  /** Initial value (uncontrolled) */
  defaultValue: PropTypes.number,
  /** Minimum value */
  min: PropTypes.number,
  /** Maximum value */
  max: PropTypes.number,
  /** Step increment */
  step: PropTypes.number,
  /** Change handler — receives new numeric value */
  onChange: PropTypes.func,
  /** Error message (shows tooltip below) */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Additional CSS class */
  className: PropTypes.string,
  /** HTML id for hidden input */
  id: PropTypes.string,
  /** HTML name for hidden input */
  name: PropTypes.string,
};

export default QuantityInput;
